import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#enableDragScrub();

  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };
  #enableDragScrub() {
  // Solo in carousel
  if (this.presentation !== 'carousel') return;

  const scroller = this.refs.slideshow?.refs?.scroller;
  if (!scroller) return;

  const slides = scroller.querySelectorAll('[style*="--slide-"]');
  const slideCount = slides.length || scroller.children.length;
  if (slideCount <= 10) return; // attivo solo per sequenze “vere”

  let dragging = false;
  let startX = 0;
  let startY = 0;
  let startIndex = 0;
  let moved = false;

  const pxPerFrame = 18;
  const threshold = 6;

  const getPoint = (e) => {
    if (e.touches?.length) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  const onDown = (e) => {
    // mouse: solo left click
    if (e.type === 'mousedown' && e.button !== 0) return;

    dragging = true;
    moved = false;

    const p = getPoint(e);
    startX = p.x;
    startY = p.y;
    startIndex = this.slideshow?.currentIndex ?? 0;

    // non bloccare subito scroll/tap: blocchiamo solo dopo threshold
    if (e.type === 'pointerdown') scroller.setPointerCapture?.(e.pointerId);
  };

  const onMove = (e) => {
    if (!dragging) return;

    const p = getPoint(e);
    const dx = p.x - startX;
    const dy = p.y - startY;

    // se sta scrollando verticalmente, non rompere
    if (!moved) {
      if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        dragging = false;
        return;
      }
      moved = true;
    }

    const delta = Math.round(dx / pxPerFrame);
    const next = Math.max(0, Math.min(slideCount - 1, startIndex + delta));

    if (next !== (this.slideshow?.currentIndex ?? 0)) {
      this.slideshow?.select(next, undefined, { animate: false });
    }

    // ora sì: evita selezioni testo/drag immagini
    e.preventDefault?.();
  };

  const onUp = () => {
    dragging = false;
  };

  // Desktop
  scroller.addEventListener('mousedown', onDown);
  window.addEventListener('mousemove', onMove, { passive: false });
  window.addEventListener('mouseup', onUp);

  // Mobile
  scroller.addEventListener('touchstart', onDown, { passive: true });
  window.addEventListener('touchmove', onMove, { passive: false });
  window.addEventListener('touchend', onUp);
}


  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
